import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, Input, Button, Space, Switch, Modal, 
  Tag, notification, Card, Tooltip, Dropdown, Menu 
} from 'antd';
import { 
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, 
  FilterOutlined, ReloadOutlined, MoreOutlined, DownloadOutlined, 
  SearchOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import packService from '../../../services/packService';
import config from '../../../config';
import { AddModal, EditModal, ViewModal } from './modals';
import userService from '../../../services/userService';
import usePermission from '../../../hooks/usePermission';
import MyTable from '../../../components/common/MyTable';

const { Search } = Input;
const { confirm } = Modal;

const PacksList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState([]);
  const { hasPermission } = usePermission();
  const [filters, setFilters] = useState({
    status: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [sortInfo, setSortInfo] = useState({
    columnKey: 'sort',
    order: 'ascend'
  });
  
  // Modal states
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState(null);

  
  const searchInputRef = useRef(null);

  useEffect(() => {
    
    fetchUsers();
  }, [filters, pagination.current, pagination.pageSize, sortInfo]);

  const fetchUsers = async () => {
    try {
      const params = { limit: 300 };
      const response = await userService.getAllCustomers(params);
      setUsers(response.data || []);
      fetchPacks();
    } catch (error) {
      notification.error({
        message: 'Error fetching users',
        description: error.message || 'Failed to load users'
      });
    }
  };
  const fetchPacks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        status: filters.status,
        sort_field: sortInfo.columnKey,
        sort_order: sortInfo.order === 'ascend' ? 'asc' : 'desc'
      };
      
      const response = await packService.getAllPacks(params);
      setPacks(response.data.data || []);
      setPagination({
        ...pagination,
        total: response.total || 0
      });
    } catch (error) {
      notification.error({
        message: 'Error fetching packs',
        description: error.message || 'Failed to load packs'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    
    // Update sortInfo if sorter has column key
    if (sorter && sorter.columnKey) {
      setSortInfo({
        columnKey: sorter.columnKey,
        order: sorter.order
      });
    } else {
      setSortInfo({ columnKey: 'sort', order: 'ascend' });
    }
  };

  //if searchTest is more than 2 characters habdleSearch
  
    useEffect(() => {
      if (searchText.length >= 2) {
        handleSearch(searchText);
       
      } else if (searchText.length === 0) {
        fetchPacks();
      }
    }, [searchText]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchPacks();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, current: 1 });
  };

  const handleResetFilters = () => {
    setFilters({ status: null });
    setSearchText('');
    setSortInfo({ columnKey: 'sort', order: 'ascend' });
    setPagination({ ...pagination, current: 1 });
    if (searchInputRef.current) {
      searchInputRef.current.input.value = '';
    }
    fetchPacks();
  };

  const showDeleteConfirm = (packId) => {
    confirm({
      title: 'Are you sure you want to delete this pack?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(packId);
      }
    });
  };

  const handleDelete = async (packId) => {
    try {
      await packService.deletePack(packId);
      notification.success({
        message: 'Pack deleted successfully'
      });
      fetchPacks();
    } catch (error) {
      notification.error({
        message: 'Error deleting pack',
        description: error.message || 'Failed to delete pack'
      });
    }
  };

  const handleStatusChange = async (checked, packId) => {
    try {
      await packService.updatePackStatus(packId, checked ? 1 : 0);
      notification.success({
        message: `Pack ${checked ? 'activated' : 'deactivated'} successfully`
      });
      
      // Update the pack status in the current list without refetching
      setPacks(prevPacks => 
        prevPacks.map(pack => 
          pack.id === packId 
            ? { ...pack, status: checked ? '1' : '0' } 
            : pack
        )
      );
    } catch (error) {
      notification.error({
        message: 'Error updating pack status',
        description: error.message || 'Failed to update pack status'
      });
    }
  };

  const handleExport = async () => {
    try {
      await packService.exportPacks();
      notification.success({
        message: 'Export started',
        description: 'Packs export has been initiated. You will be notified when it\'s ready for download.'
      });
    } catch (error) {
      notification.error({
        message: 'Error exporting packs',
        description: error.message || 'Failed to export packs'
      });
    }
  };

  // Modal handlers
  const showAddModal = () => {
    setAddModalVisible(true);
  };

  const showEditModal = (packId) => {
    setSelectedPackId(packId);
    setEditModalVisible(true);
  };

  const showViewModal = (packId) => {
    setSelectedPackId(packId);
    setViewModalVisible(true);
  };

  const handleModalSuccess = () => {
    fetchPacks();
    setAddModalVisible(false);
    setEditModalVisible(false);
  };

  const closeModals = () => {
    setAddModalVisible(false);
    setEditModalVisible(false);
    setViewModalVisible(false);
    setSelectedPackId(null);
  };

  const formatPrice = (price) => {
    return parseFloat(price || 0).toFixed(2);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      width: 80,
      fixed: 'left'
    },
    {
      title: 'Sort',
      dataIndex: 'sort',
      key: 'sort',
      sorter: true,
      width: 80
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (image, record) => (
        <div className="w-16 h-16 flex-shrink-0">
          {image ? (
            <img 
              src={`${config.APP_URL}/${image}`} 
              alt={record.name} 
              className="w-full h-full object-cover rounded border border-gray-200"
              onError={(e) => { e.target.src = '/logo.png'; }}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center text-gray-500">
              No Image
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Pack Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">
            Slug: {record.slug || 'N/A'}
          </div>
          {record.description && (
            <div className="text-xs italic text-gray-500 mt-1 max-w-xs truncate">
              {record.description}
            </div>
          )}
        </div>
      ),
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `£${formatPrice(price)}`,
      sorter: true,
      width: 120
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Switch 
          checked={status === 1 || status === '1'} 
          onChange={(checked) => handleStatusChange(checked, record.id)} 
        />
      ),
      filters: [
        { text: 'Active', value: '1' },
        { text: 'Inactive', value: '0' }
      ],
      filtered: filters.status !== null,
      filterMultiple: false,
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showViewModal(record.id)}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
          {hasPermission('edit_packs') &&  <Tooltip title="Edit Pack">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record.id)}
              className="text-yellow-500 hover:text-yellow-700"
            />
          </Tooltip>}
          {hasPermission('delete_packs') &&   <Tooltip title="Delete Pack">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record.id)}
              className="text-red-500 hover:text-red-700"
            />
          </Tooltip>}
        </Space>
      ),
      width: 120,
      fixed: 'right'
    }
  ];

  const actionsMenu = (
    <Menu>
     {/*  <Menu.Item key="export" onClick={handleExport} icon={<DownloadOutlined />}>
        Export Packs
      </Menu.Item> */}
    </Menu>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Packs Management</h1>
        <Space>
        {hasPermission('create_packs') &&   <Button
            type="primary"
            onClick={showAddModal}
            icon={<PlusOutlined />}
            className="bg-golden-navy"
          >
            Add Pack
          </Button>}
          <Dropdown overlay={actionsMenu}>
            <Button icon={<MoreOutlined />}>Actions</Button>
          </Dropdown>
        </Space>
      </div>

      <Card className="shadow-md mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <Search
              placeholder="Search packs..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              ref={searchInputRef}
            />
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleResetFilters}
              className="flex items-center"
            >
              Reset
            </Button>
          </div>

          <div className="flex gap-2">
           {/*  <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              className="flex items-center"
            >
              Export
            </Button> */}
            <Button
              type="primary"
              onClick={fetchPacks}
              icon={<ReloadOutlined />}
              className="bg-golden-navy"
            >
              Refresh
            </Button>
          </div>
        </div>

       { packs.length > 0   && <MyTable 
          columns={columns} 
          dataSource={packs} 
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} packs`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="middle"
        />}
      </Card>

      {/* Modals */}
      <AddModal 
        visible={addModalVisible}
        onCancel={closeModals}
        onSuccess={handleModalSuccess}
      />

      <EditModal 
        visible={editModalVisible}
        packId={selectedPackId}
        onCancel={closeModals}
        onSuccess={handleModalSuccess}
        userso={users}
      />

      <ViewModal 
        visible={viewModalVisible}
        packId={selectedPackId}
        onCancel={closeModals}
                userso={users}

      />
    </div>
  );
};

export default PacksList;